import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Chip,
  Stack,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  FitnessCenter as CoachingIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  PersonAdd as PersonAddIcon,
  Event as EventIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { coachingProgramInstanceAPI, amenityAPI, coachingProgramAPI } from '../../services/api';
import { CoachingProgramInstance } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';
import ReservationResultsDialog from './ReservationResultsDialog';
import { useAuth } from '../../context/AuthContext';

interface InstanceEnrollment {
  id: string;
  memberProfileId?: string;
  memberName?: string;
  memberEmail?: string;
  isGuestEnrollment: boolean;
  guestName?: string;
  guestEmail?: string;
  status: string;
  enrollmentType: 'FULL_PROGRAM' | 'DROP_IN';
  createdAt: string;
}

const ViewCoachingProgramInstancePage: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Determine referrer for breadcrumbs - check location state or fallback to calendar
  const referrer = (location.state as any)?.from || 'calendar';

  // State management
  const [programInstance, setProgramInstance] = useState<CoachingProgramInstance | null>(null);
  const [enrollments, setEnrollments] = useState<InstanceEnrollment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [cancellingInstance, setCancellingInstance] = useState<boolean>(false);
  const [missingVenues, setMissingVenues] = useState<string[]>([]);
  const missingBookingsControllerRef = useRef<AbortController | null>(null);
  const [rebooking, setRebooking] = useState<boolean>(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState<boolean>(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);

  // Abort any in-flight missing-bookings check on unmount
  useEffect(() => () => { missingBookingsControllerRef.current?.abort(); }, []);

  // Helper function to get breadcrumb path based on referrer
  const getBreadcrumbPath = () => {
    switch (referrer) {
      case 'program-management':
        return { label: 'Program Management', icon: <CoachingIcon />, path: '/coaching/programs' };
      case 'dashboard':
        return { label: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' };
      case 'calendar':
      default:
        return { label: 'Calendar', icon: <CalendarTodayIcon />, path: '/calendar' };
    }
  };

  const breadcrumbPath = getBreadcrumbPath();

  // Fetch program instance data
  useEffect(() => {
    if (instanceId) {
      fetchProgramInstance();
      fetchAmenities();
    }
  }, [instanceId]);

  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      const names = new Map<string, string>();
      response.data.forEach((amenity: any) => {
        names.set(amenity.id, amenity.name);
      });
      setVenueNames(names);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      // Don't show error - not critical
    }
  };

  const fetchProgramInstance = async () => {
    if (!instanceId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await coachingProgramInstanceAPI.getInstance(instanceId);
      const instance = response.data;
      
      setProgramInstance(instance);

      // Check for missing amenity bookings if admin
      const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin' || user.role === 'employee');
      if (isAdmin && instance.venues && instance.venues.length > 0) {
        checkMissingBookings(instance);
      }

      // Fetch enrollments for this instance
      if (instance.enrollments) {
        setEnrollments(instance.enrollments);
      } else {
        // If enrollments not included, fetch separately
        await fetchInstanceEnrollments();
      }

    } catch (error: any) {
      console.error('Error fetching program instance:', error);
      setError('Failed to load program instance details');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstanceEnrollments = async () => {
    if (!programInstance?.parentProgramId) return;

    try {
      const response = await coachingProgramAPI.getCoachingProgramEnrollments(programInstance.parentProgramId);
      const allEnrollments = response.data || [];
      
      // Filter to only enrollments for this instance
      const instanceEnrollments = allEnrollments.filter((enrollment: any) => {
        // Full program enrollments count for all instances
        if (enrollment.enrollmentType === 'FULL_PROGRAM') return true;
        // Drop-in enrollments only count if this instance is in their list
        if (enrollment.enrollmentType === 'DROP_IN' && enrollment.instanceIds) {
          return enrollment.instanceIds.includes(instanceId);
        }
        return false;
      });

      setEnrollments(instanceEnrollments);
    } catch (error: any) {
      console.error('Error fetching instance enrollments:', error);
    }
  };

  const checkMissingBookings = async (instance: CoachingProgramInstance) => {
    missingBookingsControllerRef.current?.abort();
    const controller = new AbortController();
    missingBookingsControllerRef.current = controller;

    try {
      const instances = [{
        id: instance.id,
        type: 'coachingProgramInstance' as const,
        venues: instance.venues || [],
        sessionDate: instance.sessionDate,
        startTime: instance.startTime,
        endTime: instance.endTime
      }];

      const response = await amenityAPI.checkMissingBookings(instances, controller.signal);
      const results = response.data.results || [];
      
      if (results.length > 0 && results[0].missingVenues) {
        setMissingVenues(results[0].missingVenues);
      } else {
        setMissingVenues([]);
      }
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED') return;
      console.error('Error checking for missing bookings:', error);
      setMissingVenues([]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'SCHEDULED':
        return 'success';
      case 'UPCOMING':
        return 'info';
      case 'COMPLETED':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEnrollmentStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ENROLLED':
        return 'success';
      case 'WAITLISTED':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      case 'COMPLETED':
        return 'info';
      default:
        return 'default';
    }
  };

  const getVenueNames = (venueIds?: string[]): string => {
    if (!venueIds || venueIds.length === 0) return 'No venue';
    return venueIds
      .map(id => venueNames.get(id) || id)
      .join(', ');
  };

  const canManageEnrollments = () => {
    return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  };

  const handleRemoveEnrollment = async (enrollmentId: string, enrollmentType: string) => {
    if (enrollmentType === 'FULL_PROGRAM') {
      setError('Cannot remove members enrolled in the full program from individual sessions. They must be removed from the entire program.');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this member from this session?')) {
      return;
    }

    try {
      // For drop-in enrollments, we can cancel just this instance
      await coachingProgramInstanceAPI.cancelInstanceEnrollment(instanceId!, enrollmentId);
      setSuccessMessage('Member removed from session successfully');
      fetchProgramInstance(); // Refresh data
    } catch (error: any) {
      console.error('Error removing enrollment:', error);
      setError(error.response?.data?.message || 'Failed to remove member from session');
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const handleCancelInstance = async () => {
    if (!instanceId) return;

    try {
      setCancellingInstance(true);
      await coachingProgramInstanceAPI.cancelInstance(instanceId);
      setSuccessMessage('Program instance cancelled successfully. Members have been notified.');
      setCancelDialogOpen(false);
      // Refresh the instance data
      await fetchProgramInstance();
    } catch (error: any) {
      console.error('Error cancelling instance:', error);
      setError(error.response?.data?.message || 'Failed to cancel program instance');
      setCancelDialogOpen(false);
    } finally {
      setCancellingInstance(false);
    }
  };

  const getActiveEnrollments = () => {
    return enrollments.filter(e => e.status !== 'CANCELLED' && e.status !== 'COMPLETED');
  };

  const handleRebookVenues = async () => {
    if (!instanceId || !programInstance) return;

    if (!window.confirm('This will rebook all venues for this program instance. Any existing venue bookings will be recreated. Continue?')) {
      return;
    }

    try {
      setRebooking(true);
      setError(null);

      // Call update with the current data to trigger venue rebooking
      const updateData = {
        name: programInstance.name,
        venues: programInstance.venues,
        capacity: programInstance.capacity,
        sessionDate: programInstance.sessionDate,
        startTime: programInstance.startTime,
        endTime: programInstance.endTime,
        notes: programInstance.notes,
        coachId: programInstance.coachId
      };

      const response = await coachingProgramInstanceAPI.updateInstance(instanceId, updateData);
      
      // Check if reservation result is available
      if (response.data.reservationResult) {
        setReservationDialogData({
          entityName: programInstance.name,
          operationType: 'rebook',
          reservationResult: response.data.reservationResult
        });
        setReservationDialogOpen(true);
      }
      
      // Refresh data to check if bookings were created
      await fetchProgramInstance();
      
    } catch (error: any) {
      console.error('Error rebooking venues:', error);
      setError(error.response?.data?.message || 'Failed to rebook venues');
    } finally {
      setRebooking(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !programInstance) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!programInstance) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Program instance not found</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          {referrer !== 'dashboard' && (
            <Button
              component={RouterLink}
              to={breadcrumbPath.path}
              color="inherit"
              startIcon={breadcrumbPath.icon}
              sx={{ textTransform: 'none' }}
            >
              {breadcrumbPath.label}
            </Button>
          )}
          {programInstance.parentProgramId && (
            <Button
              component={RouterLink}
              to={`/coaching/programs/${programInstance.parentProgramId}`}
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              {programInstance.name}
            </Button>
          )}
          <Typography color="text.primary">Session Details</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                <CoachingIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                {programInstance.name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Program Session • {formatDate(programInstance.sessionDate)}
              </Typography>
            </Box>
          </Box>
          {canManageEnrollments() && programInstance.status !== 'CANCELLED' && programInstance.status !== 'COMPLETED' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleOpenCancelDialog}
            >
              Cancel Session
            </Button>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Program Instance Details */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <EventIcon sx={{ mr: 1 }} />
                Session Details
                <Chip 
                  label={programInstance.status} 
                  color={getStatusColor(programInstance.status)}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarTodayIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Date"
                        secondary={formatDate(programInstance.sessionDate)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessTimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Time"
                        secondary={`${formatTimeString(programInstance.startTime)} - ${formatTimeString(programInstance.endTime)}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LocationOnIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Venue(s)"
                        secondary={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {getVenueNames(programInstance.venues)}
                              </Typography>
                              {canManageEnrollments() && programInstance.venues && programInstance.venues.length > 0 && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleRebookVenues}
                                  disabled={rebooking}
                                  sx={{ textTransform: 'none', py: 0, px: 1, fontSize: '0.75rem' }}
                                >
                                  {rebooking ? 'Rebooking...' : 'Rebook'}
                                </Button>
                              )}
                            </Box>
                            {canManageEnrollments() && missingVenues.length > 0 && (
                              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WarningIcon sx={{ fontSize: '0.9rem', color: '#ff9800' }} />
                                <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 'medium' }}>
                                  {missingVenues.length} court{missingVenues.length > 1 ? 's' : ''} missing booking{missingVenues.length > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Coach"
                        secondary={programInstance.coachName || 'Not assigned'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CoachingIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Type & Level"
                        secondary={`${programInstance.type} - ${programInstance.level}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PeopleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Capacity"
                        secondary={`${programInstance.capacity}`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {programInstance.notes && (
                <>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Notes</Typography>
                    <Typography variant="body1">{programInstance.notes}</Typography>
                  </Box>
                </>
              )}
            </Paper>

            {/* Enrollments List */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ mr: 1 }} />
                  Enrollments ({enrollments.length})
                </Typography>
              </Box>
              
              {enrollments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No enrollments for this session
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Member</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        {canManageEnrollments() && (
                          <TableCell align="right">Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {enrollments.map((enrollment) => (
                        <TableRow key={enrollment.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: '0.875rem' }}>
                                {enrollment.isGuestEnrollment
                                  ? (enrollment.guestName?.charAt(0).toUpperCase() || 'G')
                                  : (enrollment.memberName?.charAt(0).toUpperCase() || 'M')}
                              </Avatar>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2">
                                    {enrollment.isGuestEnrollment
                                      ? enrollment.guestName
                                      : enrollment.memberName}
                                  </Typography>
                                  {enrollment.isGuestEnrollment && (
                                    <Chip label="Guest" size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {enrollment.isGuestEnrollment
                                    ? enrollment.guestEmail
                                    : enrollment.memberEmail}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={enrollment.enrollmentType === 'FULL_PROGRAM' ? 'Full' : 'Drop-in'}
                              color={enrollment.enrollmentType === 'FULL_PROGRAM' ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={enrollment.status}
                              color={getEnrollmentStatusColor(enrollment.status)}
                              size="small"
                            />
                          </TableCell>
                          {canManageEnrollments() && (
                            <TableCell align="right">
                              {enrollment.status !== 'CANCELLED' && enrollment.enrollmentType === 'DROP_IN' && (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveEnrollment(enrollment.id, enrollment.enrollmentType)}
                                >
                                  Remove
                                </Button>
                              )}
                              {enrollment.enrollmentType === 'FULL_PROGRAM' && (
                                <Typography variant="caption" color="text.secondary">
                                  Full Program
                                </Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Parent Program Information */}
            {programInstance.parentProgram && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CoachingIcon sx={{ mr: 1 }} />
                  Parent Program Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Program Name</Typography>
                    <Typography variant="body1">{programInstance.parentProgram.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Type & Level</Typography>
                    <Typography variant="body1">
                      {programInstance.parentProgram.type} - {programInstance.parentProgram.level}
                    </Typography>
                  </Box>
                  {programInstance.parentProgram.description && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Description</Typography>
                      <Typography variant="body1">{programInstance.parentProgram.description}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Button
                      component={RouterLink}
                      to={`/coaching/programs/${programInstance.parentProgramId}`}
                      variant="outlined"
                      size="small"
                    >
                      View Parent Program
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Grid>

          {/* Capacity Status */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Capacity Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3" color="primary">
                    {programInstance.enrolledCount || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {programInstance.capacity} spots filled
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    Available: {(programInstance.capacity - (programInstance.enrolledCount || 0))}
                  </Typography>
                  <Typography variant="body2">
                    {Math.round(((programInstance.enrolledCount || 0) / programInstance.capacity) * 100)}% full
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(((programInstance.enrolledCount || 0) / programInstance.capacity) * 100, 100)}%`,
                      height: '100%',
                      backgroundColor: ((programInstance.enrolledCount || 0) / programInstance.capacity) >= 0.8 ? 'error.main' : 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Cancel Instance Confirmation Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={handleCloseCancelDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
              <CancelIcon sx={{ mr: 1 }} />
              Cancel Program Session
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This is an irreversible operation. Once cancelled, this session cannot be restored.
            </Alert>
            
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
              Session Details:
            </Typography>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(programInstance.sessionDate)}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong> {formatTimeString(programInstance.startTime)} - {formatTimeString(programInstance.endTime)}
              </Typography>
              <Typography variant="body2">
                <strong>Venue:</strong> {getVenueNames(programInstance.venues)}
              </Typography>
            </Box>

            {getActiveEnrollments().length > 0 ? (
              <>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  The following {getActiveEnrollments().length} member{getActiveEnrollments().length !== 1 ? 's' : ''} will be notified and unregistered from this session:
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1, mb: 2 }}>
                  <List dense>
                    {getActiveEnrollments().map((enrollment) => (
                      <ListItem key={enrollment.id} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            {enrollment.isGuestEnrollment
                              ? (enrollment.guestName?.charAt(0).toUpperCase() || 'G')
                              : (enrollment.memberName?.charAt(0).toUpperCase() || 'M')}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2">
                                {enrollment.isGuestEnrollment ? enrollment.guestName : enrollment.memberName}
                              </Typography>
                              {enrollment.isGuestEnrollment && (
                                <Chip label="Guest" size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                              )}
                              {enrollment.enrollmentType === 'FULL_PROGRAM' && (
                                <Chip label="Full Program" color="primary" size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {enrollment.isGuestEnrollment ? enrollment.guestEmail : enrollment.memberEmail}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No active enrollments for this session.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseCancelDialog}
              disabled={cancellingInstance}
            >
              Go Back
            </Button>
            <Button
              onClick={handleCancelInstance}
              color="error"
              variant="contained"
              disabled={cancellingInstance}
              startIcon={cancellingInstance ? <CircularProgress size={16} /> : <CancelIcon />}
            >
              {cancellingInstance ? 'Cancelling...' : 'Yes, Cancel Session'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reservation Results Dialog */}
        {reservationDialogData && (
          <ReservationResultsDialog
            open={reservationDialogOpen}
            onClose={() => {
              setReservationDialogOpen(false);
              setReservationDialogData(null);
            }}
            entityType="coachingProgram"
            entityName={reservationDialogData.entityName}
            operationType={reservationDialogData.operationType}
            reservationResult={reservationDialogData.reservationResult}
          />
        )}
      </Box>
    </Container>
  );
};

export default ViewCoachingProgramInstancePage;
